import { HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TvLoginStore } from '../pages/app/services/app.store';
import { LABELS } from './constants/labels.constant';
import { ClientSSO } from './entities/client-sso';
import { AppTranslateService } from './services/app-translate.service';
import { DictionaryAPIService } from './services/dictionary-api.service';
import { StoreService } from './stores/store.service';

export function createTranslateLoader(
  http: HttpClient,
  dictionaryAPIService: DictionaryAPIService,
  storeService: StoreService,
  tvLoginStore: InstanceType<typeof TvLoginStore>
) {
  return new CustomLoader(http, dictionaryAPIService, storeService, tvLoginStore);
}

class CustomLoader implements TranslateLoader {
  private translationStore = {};
  private currentApiReq;

  constructor(
    private http: HttpClient,
    private dictionaryAPIService: DictionaryAPIService,
    private storeService: StoreService,
    private tvLoginStore: InstanceType<typeof TvLoginStore>
  ) {}

  getTranslation(lang: string): Observable<any> {
    const client: ClientSSO = this.storeService.client;

    const foundTranslations = this.getTranslationsToStore(lang, client);

    if (foundTranslations) {
      return of(foundTranslations);
    }

    return from(this.getTranslationApi(lang, client)).pipe(
      tap(() => {
        this.tvLoginStore.translationLoadedForTheFirstTimeSuccessfully();
      }),
      map((labels) => {
        const result = {};

        labels.forEach((label) => {
          result[label.labelKey] = label.labelValue;
        });

        return result;
      }),
      tap((result) => {
        this.saveTranslationsToStore(lang, client, result);
        this.currentApiReq = null;
      }),
      catchError(() => {
        this.currentApiReq = null;
        return of({});
      })
    );
  }

  private getTranslationApi(lang: string, client: ClientSSO): Promise<any> {
    const params = this.getRequestParams(lang, client);

    if (this.currentApiReq) {
      return this.currentApiReq;
    }

    this.currentApiReq = this.http.post(this.dictionaryAPIService.systemLabels(), params).toPromise();

    return this.currentApiReq;
  }

  private saveTranslationsToStore(lang: string, client: ClientSSO, translations: any) {
    const key = this.getKeyForStore(lang, client);
    this.translationStore[key] = translations;
  }

  private getTranslationsToStore(lang: string, client: ClientSSO): Promise<any> {
    const key = this.getKeyForStore(lang, client);
    return this.translationStore[key];
  }

  private getKeyForStore(lang: string, client: ClientSSO): string {
    return lang + (client ? `_${client.id}` : '');
  }

  private getRequestParams(lang: string, client: ClientSSO): { [header: string]: string | string[] } {
    const params = {
      languageId: lang,
      area: '1', // hardcoded 1- Web
      keys: Object.values(LABELS),
    };

    if (client) {
      return { ...params, clientAlias: client.alias };
    }

    return params;
  }
}

@NgModule({
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient, DictionaryAPIService, StoreService, TvLoginStore],
      },
    }),
  ],
  exports: [TranslateModule],
  providers: [AppTranslateService],
})
export class AppTranslateModule {}
